"use client";
import {
  ALL_RESOURCE_ACCESS_CONFIG,
  AccessConfig,
  ResourceAccessConfig,
  ToolAccessConfig,
  UserAccess,
} from "@/lib/nextAuth/userPermissions";
import { UserReduxMetadataSchema } from "@tsTypes/next-auth";
import { Subject, Tools } from "@tsTypes/subject-schema";
import { AuthenticationStatus, BillingStatus, UserRole } from "enums/auth";
import { Session } from "next-auth";
import { signOut, useSession } from "next-auth/react";
import { deleteAllActiveTestSessions } from "../../features/activeTestSessions/activeTestSessionsSlice";
import { saveUserProgressToDB } from "../redux/helpers";
import { userHasAccessToSubject } from "./pageChecks";

export function useCheckUserAccess(
  resourceToCheck: keyof typeof ALL_RESOURCE_ACCESS_CONFIG,
  subjectDocumentId: Subject["documentId"]
): UserAccess {
  let userAccessConfig: AccessConfig = "No Access";
  const { status, data: session } = useSession();
  // if session is still loading, return loading
  if (status === "loading") {
    return "LOADING";
  }
  // first check based on account status and deny access if unauthenticated
  const userAccountStatus = getUserAuthenticationStatus(session);
  if (!session || userAccountStatus === "Unauthenticated") {
    return ALL_RESOURCE_ACCESS_CONFIG[resourceToCheck][
      "Unauthenticated"
    ] as UserAccess; // we force unauth here to deal with no session edge case and force return type since we won't ever use withSubjectAccess at teh unauth param level
  }

  // if authenticated, continue to check based on role
  else {
    const userRole = getUserRole(session)!;
    // if a tool we are checking then since access is at role level we return access based on role
    if (resourceToCheck in Tools) {
      userAccessConfig = (
        ALL_RESOURCE_ACCESS_CONFIG[resourceToCheck] as ToolAccessConfig
      )[userRole];
    }

    // else if resource is a true resource then check based on role and membership status
    else {
      // if role is not private, return relevant access based on role
      if (userRole !== "Private") {
        userAccessConfig = (
          ALL_RESOURCE_ACCESS_CONFIG[resourceToCheck] as ResourceAccessConfig
        )[userRole];
      }

      // Handle Private user role
      else {
        const privateAccess = (
          (ALL_RESOURCE_ACCESS_CONFIG[resourceToCheck] as ResourceAccessConfig)[
            "Private"
          ] as Record<string, AccessConfig>
        )[session.user.subscriptionMeta?.status ?? "none"];

        // if its a non-billing dependent access then return it
        if (typeof privateAccess === "string") {
          userAccessConfig = privateAccess;
        } else {
          // if its a billing dependent access then return the access based on billing status
          userAccessConfig = privateAccess[getUserBillingStatus(session)];
        }
      }
    }
    // for all cases, run the subject access checks to standardize the access config returned
    return runSubjectAccessChecks(
      session,
      userAccessConfig,
      subjectDocumentId,
      ALL_RESOURCE_ACCESS_CONFIG[resourceToCheck]["Unauthenticated"]
    );
  }
}

// ************************** HELPER FUNCTIONS **************************

export function getUserRole(session: Session | null): UserRole | undefined {
  if (!session) {
    return undefined;
  }
  return session.user.role;
}

export function getUserVerificationStatus(session: Session | null): boolean {
  return !session ? false : session.user.verified;
}

export function getUserBillingStatus(session: Session | null): BillingStatus {
  return session?.user.subscriptionMeta?.status === "active" ||
    session?.user.subscriptionMeta?.status === "trialing"
    ? "Paid"
    : "Failed";
}

export function getUserAuthenticationStatus(
  session: Session | null
): Extract<AuthenticationStatus, "Authenticated" | "Unauthenticated"> {
  return session
    ? session.user.verified
      ? "Authenticated"
      : "Unauthenticated"
    : "Unauthenticated";
}

function runSubjectAccessChecks(
  session: Session,
  accessConfig: AccessConfig,
  subjectDocumentId: Subject["documentId"],
  unAuthenticatedAccessLevel: AccessConfig
): UserAccess {
  if (accessConfig === "Membership Dependent") {
    return userHasAccessToSubject(session, subjectDocumentId)
      ? "Full Access"
      : (unAuthenticatedAccessLevel as UserAccess); // we fall back to unauthenticated access level if the subject is not included in the membership subject access to avoid making members more locked than a logged out user. We also overrride type since we know unauthenticated is never going to be a membership dependent access level.
  } else {
    return accessConfig;
  }
}

export async function runLogoutFunctions(
  dispatch: any,
  userDocumentId: string,
  userReduxProgressMetadata: UserReduxMetadataSchema,
  skipSaveProgress?:boolean,
) {
  if(!skipSaveProgress) {
    const localStorageProgressMeta: { key: string; documentId: string } =
    JSON.parse(localStorage.getItem("progressMeta") || "{}");
  if (localStorageProgressMeta.documentId && localStorageProgressMeta.key) {
    await saveUserProgressToDB(
      userDocumentId,
      localStorageProgressMeta.documentId,
      localStorageProgressMeta.key,
      userReduxProgressMetadata
    );
  }
  }
  localStorage.removeItem("progressMeta");
  dispatch(deleteAllActiveTestSessions());
  await signOut({
    callbackUrl: `/login`,
  });
}
